import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { client } from '../client'; // Import Sanity client

const Footer = () => {
  const [footerData, setFooterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const query = `*[_type == "footer"][0]{
      schoolName,
      address,
      phone,
      fax,
      email,
      quickLinks[]{
        _key,
        text,
        url,
        externalLink
      },
      parentResourcesLinks[]{
        _key,
        text,
        url,
        externalLink
      },
      studentResourcesLinks[]{
        _key,
        text,
        url,
        externalLink
      },
      externalResourcesLinks[]{
        _key,
        text,
        url,
        externalLink
      },
      copyrightText,
      siteCredits[]{
        _key,
        text,
        url
      }
    }`;

    client.fetch(query)
      .then((data) => {
        setFooterData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching footer data:', err);
        setError('Failed to load footer content.');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <footer className="bg-[#2a4865] text-white py-12 mt-8"><div className="max-w-7xl mx-auto px-4 text-center">Loading footer...</div></footer>;
  }

  if (error) {
    return <footer className="bg-[#2a4865] text-white py-12 mt-8"><div className="max-w-7xl mx-auto px-4 text-center">Error loading footer.</div></footer>;
  }

  if (!footerData) {
    return <footer className="bg-[#2a4865] text-white py-12 mt-8"><div className="max-w-7xl mx-auto px-4 text-center">No footer data found.</div></footer>;
  }

  const renderLink = (linkItem) => {
    if (linkItem.externalLink) {
      return (
        <a href={linkItem.url} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-yellow-400 transition-colors flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          {linkItem.text}
        </a>
      );
    }
    return (
      <Link to={linkItem.url} className="text-white/80 hover:text-yellow-400 transition-colors flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        {linkItem.text}
      </Link>
    );
  };
  
  return (
    <footer className="bg-[#2a4865] text-white py-12 mt-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-wrap justify-between mb-12">
          <div className="flex-1 min-w-[250px] mb-8 md:mb-0 md:pr-8">
            <div className="flex items-center mb-6">
              <img 
                src="/images/navbar/logo.png" // Assuming logo remains static for now
                alt={`${footerData.schoolName} Logo`} 
                className="h-20 w-20 mr-4 object-contain" 
              />
              <div>
                <h3 className="text-2xl font-bold mb-1 text-white">{footerData.schoolName ? footerData.schoolName.split(' John C. Thompson School')[0] : 'P.S. 052'}</h3>
                <p className="text-white/90 font-medium">{footerData.schoolName ? footerData.schoolName.split(' ').slice(2).join(' ') : 'John C. Thompson School'}</p>
              </div>
            </div>
            {footerData.address && (
              <p className="mb-6 text-white/80 leading-relaxed">
                {footerData.address}
              </p>
            )}
            {footerData.phone && (
              <div className="flex items-center mb-1 text-white/80">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>{footerData.phone}</span>
              </div>
            )}
            {footerData.fax && (
              <div className="flex items-center text-white/80">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20" />
                </svg>
                <span>{footerData.fax}</span>
              </div>
            )}
          </div>
          
          {footerData.quickLinks && footerData.quickLinks.length > 0 && (
            <div className="flex-1 min-w-[180px] mb-8 md:mb-0">
              <h4 className="text-lg font-bold mb-6 text-white relative pb-2 inline-block">
                Quick Links
                <span className="absolute bottom-0 left-0 w-10 h-0.5 bg-yellow-500"></span>
              </h4>
              <ul className="list-none p-0 m-0 space-y-3">
                {footerData.quickLinks.map(linkItem => (
                  <li key={linkItem._key}>{renderLink(linkItem)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Placeholder for Parent Resources - Add if data exists */}
          {footerData.parentResourcesLinks && footerData.parentResourcesLinks.length > 0 && (
             <div className="flex-1 min-w-[180px] mb-8 md:mb-0">
               <h4 className="text-lg font-bold mb-6 text-white relative pb-2 inline-block">
                 Parent Resources
                 <span className="absolute bottom-0 left-0 w-10 h-0.5 bg-yellow-500"></span>
               </h4>
               <ul className="list-none p-0 m-0 space-y-3">
                 {footerData.parentResourcesLinks.map(linkItem => (
                   <li key={linkItem._key}>{renderLink(linkItem)}</li>
                 ))}
               </ul>
             </div>
          )}
          
          {footerData.externalResourcesLinks && footerData.externalResourcesLinks.length > 0 && (
            <div className="flex-1 min-w-[180px]">
              <h4 className="text-lg font-bold mb-6 text-white relative pb-2 inline-block">
                External Resources
                <span className="absolute bottom-0 left-0 w-10 h-0.5 bg-yellow-500"></span>
              </h4>
              <ul className="list-none p-0 m-0 space-y-3">
                {footerData.externalResourcesLinks.map(linkItem => (
                  <li key={linkItem._key}>{renderLink(linkItem)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-white/10">
          {footerData.copyrightText && (
            <p className="text-sm text-white/70 mb-4 md:mb-0">
              {footerData.copyrightText.replace('{currentYear}', currentYear.toString())}
            </p>
          )}
          <div className="flex gap-4">
            {/* Social media links - these could also be moved to Sanity if needed */}
            <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#691e31] transition-colors" aria-label="Facebook">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
              </svg>
            </a>
            <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#691e31] transition-colors" aria-label="Twitter">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
              </svg>
            </a>
            <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#691e31] transition-colors" aria-label="Instagram">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 